import { useEffect, useMemo, useState } from "react";
import { Button, Modal, Switch } from "antd";
import Title from "antd/es/typography/Title";
import { InfoCircleOutlined } from "@ant-design/icons";
import { keyLabelMap } from "../../screens/History/HistoryUtils";

const MAX_FROZEN_COLUMNS = 3;

function ColumnRow({ item, onToggle }) {
  return (
    <div className="field-item">
      <div className="field-item-label">{item.label}</div>
      <Switch
        size="small"
        id={`freeze-sw-${item.id}`}
        checked={item.freezeOrder !== null}
        onChange={() => onToggle(item.id)}
        className="field-item-switch"
      />
    </div>
  );
}

export const HistoryFreezeColumns = ({
  open,
  setOpen,
  messageToast,
  selectedColumns = [],
  setSelectedColumns,
  frozenColumns = [],
  setFrozenColumns,
  onPreferenceAction,
}) => {
  const [items, setItems] = useState([]);

  // Initialize/refresh component items based on visible columns and frozen columns states
  useEffect(() => {
    if (open) {
      const configColumns = selectedColumns
        .map((colKey, index) => {
          const isFrozen = frozenColumns.includes(colKey);
          const label = keyLabelMap[colKey] || colKey;

          // Frozen order follows selectedColumns, the same source the table renders from.
          return {
            id: colKey,
            label: label,
            freezeOrder: isFrozen ? index + 1 : null,
            displayOrder: index + 1,
          };
        })
        .sort((a, b) => {
          const orderA =
            a.freezeOrder !== null
              ? a.freezeOrder
              : a.displayOrder + 10000;
          const orderB =
            b.freezeOrder !== null
              ? b.freezeOrder
              : b.displayOrder + 10000;
          return orderA - orderB;
        })
        .map((column, index) => ({
          ...column,
          freezeOrder: column.freezeOrder !== null ? index + 1 : null,
        }));

      // Enforce max freeze limit
      let frozenCount = 0;
      const limitedColumns = configColumns.map((column) => {
        if (column.freezeOrder === null) {
          return column;
        }
        frozenCount += 1;
        if (frozenCount > MAX_FROZEN_COLUMNS) {
          return { ...column, freezeOrder: null };
        }
        return { ...column, freezeOrder: frozenCount };
      });

      setItems(limitedColumns);
    }
  }, [open, selectedColumns, frozenColumns]);

  const frozen = useMemo(
    () =>
      items
        .filter((item) => item.freezeOrder !== null)
        .sort((a, b) => a.freezeOrder - b.freezeOrder),
    [items],
  );
  const unfrozen = useMemo(
    () => items.filter((item) => item.freezeOrder === null),
    [items],
  );

  const reassignFreezeOrder = (list) =>
    list.map((item, index) => ({ ...item, freezeOrder: index + 1 }));

  const toggleItem = (id) => {
    const target = items.find((item) => item.id === id);
    if (!target) return;

    setItems((previous) => {
      // Unfreeze is always allowed.
      if (target.freezeOrder !== null) {
        const updated = previous.map((item) =>
          item.id === id ? { ...item, freezeOrder: null } : item,
        );
        const nextFrozen = updated
          .filter((item) => item.freezeOrder !== null)
          .sort((a, b) => a.freezeOrder - b.freezeOrder);

        const reassigned = reassignFreezeOrder(nextFrozen);
        const reassignedIds = reassigned.map((item) => item.id);

        return updated.map((item) => {
          const index = reassignedIds.indexOf(item.id);
          if (index !== -1) {
            return reassigned[index];
          }
          return { ...item, freezeOrder: null };
        });
      }

      // Enforce freeze limit.
      const currentFrozenCount = previous.filter(
        (item) => item.freezeOrder !== null,
      ).length;

      if (currentFrozenCount >= MAX_FROZEN_COLUMNS) {
        messageToast(
          "warning",
          `You can freeze up to ${MAX_FROZEN_COLUMNS} columns at a time.`,
          "freeze-limit",
        );
        return previous;
      }

      const maxOrder = Math.max(
        0,
        ...previous.map((entry) => entry.freezeOrder || 0),
      );

      return previous.map((item) =>
        item.id === id ? { ...item, freezeOrder: maxOrder + 1 } : item,
      );
    });
  };

  function applyFun() {
    const frozenKeys = frozen.map((item) => item.id);
    // Frozen columns render as a contiguous left block, so keep selectedColumns
    // (the order the table and the API persist) matching that display order.
    const reorderedColumns = [
      ...frozenKeys,
      ...selectedColumns.filter((colKey) => !frozenKeys.includes(colKey)),
    ];
    // The success/error toast is raised by the grid preferences save this triggers.
    // Signal the singular/plural/none variant so the toast wording matches the count.
    let freezeAction = "freeze";
    if (frozenKeys.length === 0) {
      freezeAction = "freezeNone";
    } else if (frozenKeys.length > 1) {
      freezeAction = "freezeMultiple";
    }
    onPreferenceAction?.(freezeAction);
    setSelectedColumns(reorderedColumns);
    setFrozenColumns(frozenKeys);
    setOpen(false);
  }

  return (
    <Modal
      open={open}
      okText="Apply"
      cancelText="Cancel"
      onCancel={() => setOpen(false)}
      onOk={applyFun}
      width={800}
      style={{
        top: 40,
      }}
      footer={[
        <Button key="back" type="text" onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={applyFun}
        >
          Apply
        </Button>,
      ]}
    >
      <div style={{ padding: "25px" }}>
        <div
          style={{ marginBottom: "20px", borderBottom: "solid 1px #80808047" }}
        >
          <Title level={5}>Freeze Columns</Title>
          <p style={{ margin: "0 0 12px", color: "#737373", fontSize: "13px" }}>
            You can freeze up to {MAX_FROZEN_COLUMNS} columns at a time.
          </p>
          <p
            style={{
              margin: "0 0 12px",
              display: "flex",
              alignItems: "flex-start",
              gap: "6px",
              padding: "8px 12px",
              background: "#f0f5ff",
              border: "1px solid #adc6ff",
              borderRadius: "6px",
              color: "#1d39c4",
              fontSize: "12px",
            }}
          >
            <InfoCircleOutlined style={{ marginTop: "2px" }} />
            <span>
              Hidden columns are excluded from freeze management. Make a column
              visible from Add Columns to freeze it.
            </span>
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "20px",
          }}
        >
          <div>
            <p className="items-section-title">Frozen</p>
            <div className="fields-overlay-drag-container">
              {frozen.length ? (
                frozen.map((item) => (
                  <ColumnRow key={item.id} item={item} onToggle={toggleItem} />
                ))
              ) : (
                <p className="empty-item">
                  No frozen columns. Toggle columns on the right.
                </p>
              )}
            </div>
          </div>

          <div>
            <p className="items-section-title">Unfrozen</p>
            <div className="fields-overlay-drag-container">
              {unfrozen.length ? (
                unfrozen.map((item) => (
                  <ColumnRow key={item.id} item={item} onToggle={toggleItem} />
                ))
              ) : (
                <p className="empty-item">All columns are frozen.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
